import React, { useContext, useEffect, useCallback } from 'react';
import {
  Button, CardRounded, CardShowcase, Heading, IconDollar, IconFeedback, IconSuccess, IconTelephone,
} from '@americanexpress/dls-react';
import { withRouter } from '@americanexpress/one-app-router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import Wrapper from '../components/Wrapper';
import { BankContext } from '../utility/BankContext';
import { AmountSelectedContext } from '../utility/AmountSelectedContext';
import { DateContext } from '../utility/DateContext';
import { paymentTypes } from '../assets/statementData';
import useApiWithSession from '../utility/useApiWithSession';
import { PaymentContext } from '../utility/PaymentContext';
import { TokenContext } from '../utility/TokenContext';
import { formatDisplayDate } from '../utility/dateUtils';

// Extracted reusable fragment for payment details
const PaymentDetailsFragment = ({
  confirmationCode, selectedBank, amountSelected, paymentType, selectedDate,
}) => (
  <div className="pad-1-lr pad-2-tb card-rounded flex flex-column flex-align-start" style={{ gap: '1rem' }}>
    <div className="flex flex-column width-full text-align-left">
      <p className="heading-3 font-weight-bold text-align-center">{confirmationCode}</p>
      <p className="legal-1 text-align-center">Confirmation Code</p>
    </div>
    <hr />
    <div className="flex flex-column width-full text-align-left">
      <p className="legal-1">Bank Account</p>
      <p className="body-3">
        {selectedBank?.financialInstitutionAccount?.institutionName}-
        {selectedBank?.financialInstitutionAccount?.accountIdentifierRedacted}
      </p>
    </div>
    <hr />
    <div className="flex flex-column width-full text-align-left">
      <p className="legal-1">Amount</p>
      <p className="body-3">
        ${Number(amountSelected).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <br /> {paymentTypes[paymentType]}
      </p>
    </div>
    <hr />
    <div className="flex flex-column width-full text-align-left">
      <p className="legal-1">Date</p>
      <p className="body-3">{formatDisplayDate(selectedDate)}</p>
    </div>
  </div>
);

PaymentDetailsFragment.propTypes = {
  confirmationCode: PropTypes.string,
  selectedBank: PropTypes.shape({
    financialInstitutionAccount: PropTypes.shape({
      institutionName: PropTypes.string,
      accountIdentifierRedacted: PropTypes.string,
    }),
  }),
  amountSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paymentType: PropTypes.string,
  selectedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

const Finish = ({ router }) => {
  // Contexts
  const dispatch = useDispatch();
  const { selectedBank } = useContext(BankContext);
  const { amountSelected, paymentType } = useContext(AmountSelectedContext);
  const { selectedDate } = useContext(DateContext);
  const {
    market1, journey1, sid1, u1, paymentSchedule, confirmationCode, setJourney1, env,
  } = useContext(PaymentContext);
  const { gateWayUrl } = useContext(TokenContext);

  // API hooks

  const {
    isLoading, data, error, run,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'FINISH_OK' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });
  const {
    isLoading: isLoadingFinish, data: dataFinish, error: errorFinish, run: runFinish,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'FINISH_LOAD_OK' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });
  const {
    isLoading: isLoadingCall, error: errorCall, data: dataCall, run: runPhoneCall,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'FINISH_CONTINUE_PHONE' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });
  const {
    isLoading: isLoadingRate, error: errorRate, data: dataRate, run: runRateExperience,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'FINISH_RATE_EXPERIENCE' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });
  const {
    isLoading: isLoadingBalanceLink,
    data: dataBalanceLink,
    error: errorBalanceLink,
    run: runBalanceLink,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'FINISH_BALANCE' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });

  // Navigation effects for API responses
  useEffect(() => {
    if (isLoading === false) {
      if (error || data?.statusCode === 'failure') {
        dispatch(addErrorToReport(error || new Error('FINISH_OK failed'), { message: 'Finish: FINISH_OK event failed.' }));
        router.push({ pathname: '/message', query: { level: 3 } });
      } else if (data?.statusCode === 'success') {
        router.push({ pathname: '/message', query: { level: 1 } });
      }
    }
  }, [isLoading, error, data, dispatch, router]);

  useEffect(() => {
    if (isLoadingFinish === false && (errorFinish || dataFinish?.statusCode === 'failure')) {
      dispatch(addErrorToReport(errorFinish || new Error('FINISH_LOAD_OK failed'), { message: 'Finish: FINISH_LOAD_OK event failed.' }));
      router.push({ pathname: '/message', query: { level: 3 } });
    }
  }, [isLoadingFinish, errorFinish, dataFinish, dispatch, router]);

  useEffect(() => {
    if (isLoadingCall === false) {
      if (!errorCall && dataCall?.statusCode === 'success') {
        router.push({ pathname: '/message', query: { level: 1 } });
      } else if (errorCall || dataCall?.statusCode === 'failure') {
        dispatch(addErrorToReport(errorCall || new Error('FINISH_CONTINUE_PHONE failed'), { message: 'Finish: FINISH_CONTINUE_PHONE event failed.' }));
        router.push({ pathname: '/message', query: { level: 3 } });
      }
    }
  }, [isLoadingCall, errorCall, dataCall, dispatch, router]);

  useEffect(() => {
    if (isLoadingRate === false) {
      if (!errorRate && dataRate?.statusCode === 'success') {
        router.push({ pathname: '/feedback' });
      } else if (errorRate || dataRate?.statusCode === 'failure') {
        dispatch(addErrorToReport(errorRate || new Error('FINISH_RATE_EXPERIENCE failed'), { message: 'Finish: FINISH_RATE_EXPERIENCE event failed.' }));
        router.push({ pathname: '/message', query: { level: 3 } });
      }
    }
  }, [isLoadingRate, errorRate, dataRate, dispatch, router]);

  // Initial load event
  useEffect(() => {
    const sendFinishLoadEvent = async () => {
      const sessionId = document.cookie
        .split('; ')
        .find((row) => row.startsWith('session_id='))
        ?.split('=')[1];
      if (!sessionId || !confirmationCode) {
        router.push({ pathname: '/message', query: { level: 3 } });
        return;
      }
      await runFinish();
    };
    sendFinishLoadEvent();
  }, []);

  useEffect(() => {
    if (isLoadingBalanceLink === false && (errorBalanceLink || dataBalanceLink?.statusCode === 'failure')) {
      router.push({
        pathname: '/message',
        query: {
          level: 3,
        },
      });
    } else if (isLoadingBalanceLink === false && !errorBalanceLink && dataBalanceLink?.statusCode === 'success') {
      setJourney1('bal');
      router.push({
        pathname: '/',
      });
    }
  }, [isLoadingBalanceLink]);

  // Disable browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      window.history.go(1);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Event handlers
  const handleFinish = useCallback(async () => {
    const sessionId = document.cookie
      .split('; ')
      .find((row) => row.startsWith('session_id='))
      ?.split('=')[1];
    if (!sessionId) return;
    await run();
  }, [run]);

  const phoneCall = useCallback(async () => {
    const sessionId = document.cookie
      .split('; ')
      .find((row) => row.startsWith('session_id='))
      ?.split('=')[1];
    if (!sessionId) return;
    await runPhoneCall();
  }, [runPhoneCall]);

  const redirectToBalance = async () => {
    await runBalanceLink();
  };

  // Render
  return (
    <Wrapper>
      <div className="width-full">
        <CardShowcase className="anim-fade-in anim-delay-1 width-full pad-1 card-rounded margin-2-t">
          <div className="pad-1-lr pad-2-tb card-rounded flex flex-column flex-align-start" style={{ gap: '1rem' }}>
            <div className="flex flex-column width-full text-align-center pad-3-lr" style={{ gap: '1rem' }}>
              <IconSuccess filled={true} title="check icon" titleId="check-icon-id" size="lg" className="dls-color-success" />
              <Heading headingStyle="heading4" level={3} style={{ fontWeight: 700 }}>Thank you for your payment!</Heading>
              <p className="legal-3">
                {paymentSchedule
                  ? 'Your payment has been scheduled successfully. You will receive a confirmation shortly.'
                  : 'You have submitted your payment and your account balance will update shortly.'}
              </p>
            </div>
          </div>
        </CardShowcase>
        <CardShowcase className="anim-fade-in anim-delay-1 width-full pad-1 card-rounded">
          <PaymentDetailsFragment
            confirmationCode={confirmationCode}
            selectedBank={selectedBank}
            amountSelected={amountSelected}
            paymentType={paymentType}
            selectedDate={selectedDate}
          />
          <Button
            type="button"
            className="width-full margin-1-t"
            styleType="primary"
            style={{ maxWidth: '100%' }}
            onClick={handleFinish}
          >
            <p className="heading-3">Done</p>
          </Button>
        </CardShowcase>
        <Heading level={4} headingStyle="heading3" className="pad-1-lr">Helpful Links</Heading>
        <CardRounded className="anim-fade-in anim-delay-1 width-full pad-1 card-rounded">
          <div className="text-align-left">
            {env && (
              <>
                <div className="flex width-full pad-1">
                  <IconDollar title="Example icon" titleId="example-icon-id" size="sm" />
                  <div className="text-align-left margin-1-l body-3">
                    <button
                      onClick={redirectToBalance}
                      type="button"
                      className="text-link link-underlined"
                      style={{ textDecoration: 'underline' }}
                    >
                      <span>View Balance Summary</span>
                    </button>
                  </div>
                </div>
                <hr />
              </>
            ) }
            <div className="flex width-full pad-1">
              <IconTelephone title="Example icon" titleId="example-icon-id" size="sm" />
              <button type="button" className="text-link link-underlined text-align-left margin-1-l body-3" style={{ textDecoration: 'underline' }} onClick={phoneCall}><span>Continue on Phone</span></button>
            </div>
          </div>
        </CardRounded>
        <div className="flex pad-1-lr">
          <IconFeedback filled={true} title="Example icon" titleId="example-icon-id" className="dls-bright-blue" />
          <p className="pad-1-lr body-1">We’d love to hear from you.</p>
        </div>
        <CardRounded className="anim-fade-in anim-delay-1 width-full pad-1 card-rounded">
          <button
            type="button"
            className="text-link link-underlined pad-1 text-align-left card-rounded"
            style={{ textDecoration: 'underline' }}
            onClick={runRateExperience}
          >
            <span>Rate Your Experience</span>
          </button>
        </CardRounded>
      </div>
    </Wrapper>
  );
};

Finish.propTypes = {
  router: PropTypes.instanceOf(Object).isRequired,
};

export default withRouter(Finish);
